import { useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { buildFaqHref } from '../data/faqData';
import { FaqExplorer } from './faq/FaqExplorer';
import './FaqModal.css';

interface FaqModalProps {
  open: boolean;
  onClose: () => void;
  initialQuery?: string;
  initialItemId?: string;
}

export function FaqModal({ open, onClose, initialQuery = '', initialItemId = '' }: FaqModalProps) {
  const { t } = useTranslation();
  const onKey = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose],
  );

  useEffect(() => {
    if (!open) return;
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    const prevPosition = document.body.style.position;
    const prevTop = document.body.style.top;
    const prevWidth = document.body.style.width;
    const scrollY = window.scrollY;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      document.body.style.position = prevPosition;
      document.body.style.top = prevTop;
      document.body.style.width = prevWidth;
      window.scrollTo(0, scrollY);
    };
  }, [open, onKey]);

  if (!open) return null;

  return (
    <div
      className="saas-faq-modal-overlay"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="saas-faq-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="saas-faq-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="saas-faq-modal__head">
          <h2 id="saas-faq-modal-title" className="saas-faq-modal__title">
            <span aria-hidden="true">🌐</span> {t('faq.modalTitle')}
          </h2>
          <div className="saas-faq-modal__actions">
            <Link
              to={buildFaqHref({ id: initialItemId, q: initialQuery })}
              className="saas-faq-modal__full"
              onClick={onClose}
            >
              {t('faq.modalFull')}
            </Link>
            <button
              type="button"
              className="saas-faq-modal__close"
              onClick={onClose}
              aria-label={t('faq.closeHelp')}
            >
              ×
            </button>
          </div>
        </div>
        <div className="saas-faq-modal__body">
          <FaqExplorer
            key={`${initialItemId}-${initialQuery}`}
            variant="modal"
            initialQuery={initialQuery}
            initialItemId={initialItemId}
          />
        </div>
      </div>
    </div>
  );
}

interface FaqHelpFabProps {
  onOpen: () => void;
}

export function FaqHelpFab({ onOpen }: FaqHelpFabProps) {
  const { t } = useTranslation();
  return (
    <button type="button" className="saas-faq-fab" onClick={onOpen} title={t('nav.faq')}>
      <span aria-hidden="true">❓</span>
      <span className="saas-faq-fab__label">{t('faq.fabLabel')}</span>
    </button>
  );
}
